import React from 'react';
import { ExtractoBancarioButton } from '../../../components/analista/ExtractoBancario';
import { EmptyState, LoadingState } from '../../../components/common/EmptyState';
import { PeriodSelector } from '../../../components/common/PeriodSelector';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { TabsContent } from '../../../components/ui/tabs';
import { facturacionApi } from '../../../lib/api';
import { cn, formatImporte } from '../../../lib/utils';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, CreditCard, Loader2, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';

// Componente para editar fecha de cobro con formato dd/mm/aaaa
const FechaCobro = ({ initialValue, onSave }) => {
  const [raw, setRaw] = React.useState('');
  const inputRef = React.useRef(null);

  const isoToRaw = (iso) => {
    if (!iso) return '';
    const p = iso.split('-');
    if (p.length === 3) return `${p[2]}${p[1]}${p[0]}`;
    return '';
  };

  React.useEffect(() => { setRaw(isoToRaw(initialValue)); }, [initialValue]);

  const formatDisplay = (r) => {
    if (!r) return '';
    if (r.length <= 2) return r;
    if (r.length <= 4) return `${r.slice(0,2)}/${r.slice(2)}`;
    return `${r.slice(0,2)}/${r.slice(2,4)}/${r.slice(4)}`;
  };

  const handleChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
    setRaw(digits);
  };

  const handleSave = async () => {
    if (raw.length === 8) {
      const day = raw.slice(0,2);
      const month = raw.slice(2,4);
      const year = raw.slice(4,8);
      const dateObj = new Date(`${year}-${month}-${day}`);
      if (!isNaN(dateObj.getTime())) {
        const isoDate = `${year}-${month}-${day}`;
        if (isoDate !== initialValue) await onSave(isoDate);
        return;
      }
    }
    if (raw === '' && initialValue) { await onSave(''); }
    else if (raw !== '' && raw.length < 8) { setRaw(isoToRaw(initialValue)); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      handleSave();
      inputRef.current?.blur();
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (!document.hasFocus()) return;
      handleSave();
    }, 150);
  };

  return (
    <Input ref={inputRef} type="text" value={formatDisplay(raw)} onChange={handleChange}
      onBlur={handleBlur} onKeyDown={handleKeyDown}
      placeholder="dd/mm/aaaa" className="h-7 text-xs w-[95px] text-center" />
  );
};

// Celda editable de bonificación de intereses
const BonificacionCell = ({ item, edificioId, selectedPeriodo, setFacturacionData, toast, formatImporte }) => {
  const [editing, setEditing] = React.useState(false);
  const [value, setValue] = React.useState('');
  const inputRef = React.useRef(null);
  const bonif = item.bonificacion_intereses || 0;

  const handleEdit = () => {
    setValue(String(bonif || item.intereses || 0));
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 50);
  };

  const handleSave = async () => {
    const nuevo = parseFloat(value);
    if (isNaN(nuevo) || nuevo < 0) return;
    if (nuevo === bonif) return;
    try {
      await facturacionApi.updateStatus(edificioId, item.unidad_id, selectedPeriodo, { bonificacion_intereses: nuevo });
      const res = await facturacionApi.getMensual(edificioId, selectedPeriodo);
      setFacturacionData(res.data);
      toast.success('Bonificacion actualizada');
    } catch (err) { toast.error('Error al guardar'); }
  };

  const handleBlurBonif = () => {
    setTimeout(() => {
      if (!document.hasFocus()) return;
      handleSave();
      setEditing(false);
    }, 150);
  };

  const handleKeyDownBonif = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); handleSave(); setEditing(false); }
    if (e.key === 'Escape') setEditing(false);
  };

  if (editing) {
    return (
      <Input ref={inputRef} type="number" value={value} onChange={(e) => setValue(e.target.value)}
        onBlur={handleBlurBonif} onKeyDown={handleKeyDownBonif}
        className="h-7 text-xs w-[100px] text-right font-mono" data-testid={`bonif-input-${item.unidad_id}`} />
    );
  }

  return (
    <span className="inline-flex items-center gap-1">
      {bonif > 0 ? (
        <>
          <span className="text-purple-600">- $ {formatImporte(bonif, 2)}</span>
          <button onClick={handleEdit} className="text-purple-400 hover:text-purple-600 transition-colors" title="Editar bonificacion">
            <Pencil className="w-3 h-3" />
          </button>
        </>
      ) : (
        <button onClick={handleEdit} className="text-slate-300 hover:text-purple-500 transition-colors" title="Agregar bonificacion">
          <Pencil className="w-3 h-3" />
        </button>
      )}
    </span>
  );
};


// Sub-filas de cobros parciales
const CobrosParciales = ({ item, edificioId, selectedPeriodo, setFacturacionData, toast }) => {
  const [newMonto, setNewMonto] = React.useState('');
  const [newFecha, setNewFecha] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const cobros = item.cobros_parciales || [];
  const totalCobrado = cobros.reduce((sum, c) => sum + (c.monto || 0), 0);
  const saldo = (item.total || 0) - totalCobrado;

  const handleAdd = async () => {
    const monto = parseFloat(newMonto);
    if (isNaN(monto) || monto <= 0) { toast.error('Ingresá un monto válido'); return; }
    if (!newFecha || newFecha.length !== 10) { toast.error('Ingresá una fecha válida'); return; }
    setSaving(true);
    try {
      // Parse dd/mm/yyyy to yyyy-mm-dd
      const parts = newFecha.split('/');
      const isoFecha = `${parts[2]}-${parts[1].padStart(2,'0')}-${parts[0].padStart(2,'0')}`;
      await facturacionApi.addCobroParcial(edificioId, item.unidad_id, selectedPeriodo, { monto, fecha: isoFecha });
      const res = await facturacionApi.getMensual(edificioId, selectedPeriodo);
      setFacturacionData(res.data);
      setNewMonto('');
      setNewFecha('');
      toast.success('Cobro parcial registrado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al registrar cobro');
    }
    setSaving(false);
  };

  const handleDelete = async (cobroId) => {
    try {
      await facturacionApi.deleteCobroParcial(edificioId, item.unidad_id, cobroId, selectedPeriodo);
      const res = await facturacionApi.getMensual(edificioId, selectedPeriodo);
      setFacturacionData(res.data);
      toast.success('Cobro eliminado');
    } catch (err) {
      toast.error('Error al eliminar cobro');
    }
  };

  const handleFechaChange = (e) => {
    let input = e.target.value.replace(/[^\d/]/g, '');
    const numbersOnly = input.replace(/\//g, '');
    let formatted = '';
    for (let i = 0; i < numbersOnly.length && i < 8; i++) {
      formatted += numbersOnly[i];
      if ((i === 1 || i === 3) && i < numbersOnly.length - 1) formatted += '/';
    }
    if (numbersOnly.length === 2 || numbersOnly.length === 4) formatted += '/';
    setNewFecha(formatted);
  };

  return (
    <tr>
      <td colSpan={9} className="p-0">
        <div className="bg-amber-50/50 border-t border-amber-200 px-6 py-3">
          {/* Resumen */}
          <div className="flex items-center gap-4 mb-2 text-xs">
            <span className="text-slate-500">Cobrado: <span className="font-semibold text-green-700">$ {formatImporte(totalCobrado, 2)}</span></span>
            <span className="text-slate-300">|</span>
            <span className="text-slate-500">Pendiente: <span className={cn("font-semibold", saldo > 0 ? "text-red-600" : "text-green-600")}>$ {formatImporte(Math.max(saldo, 0), 2)}</span></span>
          </div>
          {/* Lista de cobros */}
          {cobros.length > 0 && (
            <table className="w-full text-xs mb-2">
              <thead>
                <tr className="text-slate-400">
                  <th className="text-left py-1 font-medium w-[30%]">Monto</th>
                  <th className="text-left py-1 font-medium w-[30%]">Fecha</th>
                  <th className="w-[10%]"></th>
                </tr>
              </thead>
              <tbody>
                {cobros.map((c) => (
                  <tr key={c.cobro_id} className="border-t border-amber-100">
                    <td className="py-1.5 font-mono text-slate-700">$ {formatImporte(c.monto, 2)}</td>
                    <td className="py-1.5 text-slate-600">
                      {c.fecha ? new Date(c.fecha + 'T12:00:00').toLocaleDateString('es-AR') : '-'}
                    </td>
                    <td className="py-1.5 text-right">
                      <button
                        onClick={() => handleDelete(c.cobro_id)}
                        className="text-slate-300 hover:text-red-500 transition-colors"
                        title="Eliminar cobro"
                        data-testid={`delete-cobro-${c.cobro_id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {/* Agregar nuevo cobro */}
          <div className="flex items-center gap-2 pt-1 border-t border-amber-200">
            <Input
              type="number"
              placeholder="Monto"
              value={newMonto}
              onChange={(e) => setNewMonto(e.target.value)}
              className="h-7 text-xs w-[130px]"
              data-testid={`new-cobro-monto-${item.unidad_id}`}
            />
            <Input
              type="text"
              placeholder="dd/mm/aaaa"
              value={newFecha}
              onChange={handleFechaChange}
              className="h-7 text-xs w-[105px] text-center"
              data-testid={`new-cobro-fecha-${item.unidad_id}`}
            />
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs gap-1"
              onClick={handleAdd}
              disabled={saving}
              data-testid={`add-cobro-btn-${item.unidad_id}`}
            >
              <Plus className="w-3 h-3" />
              Agregar
            </Button>
          </div>
        </div>
      </td>
    </tr>
  );
};

export const TabCobro = ({ cn, edificio, edificioId, error, facturacionData, formatImporte, handleSyncColppy, getGroupedFacItems, expandedGrupos = {}, toggleGrupoExpanded, loadingFacturacion, navigatePeriodo, selectedPeriodo, setFacturacionData, setSelectedPeriodo, syncingColppy, toast, unidades }) => {
  const [expandedRows, setExpandedRows] = React.useState({});

  const toggleRow = (unidadId) => {
    setExpandedRows(prev => ({ ...prev, [unidadId]: !prev[unidadId] }));
  };

  // Auto-expand rows that are in cobro_parcial state
  React.useEffect(() => {
    if (facturacionData?.items) {
      const autoExpand = {};
      facturacionData.items.forEach(item => {
        if (item.estado_factura === 'cobro_parcial') {
          autoExpand[item.unidad_id] = true;
        }
      });
      setExpandedRows(prev => ({ ...prev, ...autoExpand }));
    }
  }, [facturacionData]);

  // KPIs del período: Total (cobros esperados), Pendientes (no cobrados),
  // Vencidos (no cobrados que ya pasaron de término según la lógica del semáforo).
  // Filtro por KPI: 'todos' | 'pendientes' (estado Pendiente) | 'vencidos' (sin cobrar y fuera de término).
  const [filtroKpi, setFiltroKpi] = React.useState('todos');
  const esVencido = React.useCallback((it) => {
    if (it.estado_factura === 'cobrada') return false;
    if (it.estado_factura === 'vencida') return true;
    const [pYear, pMonth] = (selectedPeriodo || '').split('-').map(Number);
    const hoy = new Date();
    const mesActual = hoy.getFullYear() * 12 + hoy.getMonth() + 1;
    const mesPeriodo = (pYear || 0) * 12 + (pMonth || 0);
    return mesPeriodo < mesActual || (mesPeriodo === mesActual && hoy.getDate() >= 16);
  }, [selectedPeriodo]);
  const esPendiente = (it) => (it.estado_factura || 'pendiente') === 'pendiente';
  const pasaFiltro = (it) => filtroKpi === 'todos' || ((it.total || 0) > 0 && (filtroKpi === 'pendientes' ? esPendiente(it) : esVencido(it)));

  const cobroKpis = React.useMemo(() => {
    const items = (facturacionData?.items || []).filter(it => it.tiene_contrato && (it.total || 0) > 0);
    const montoFinal = (it) => (it.total || 0) - (it.bonificacion_intereses || 0);
    const noCobradas = items.filter(it => it.estado_factura !== 'cobrada');
    const cobradasArr = items.filter(it => it.estado_factura === 'cobrada');
    return {
      total: items.length,
      pendientes: items.filter(it => (it.estado_factura || 'pendiente') === 'pendiente').length,
      vencidos: items.filter(esVencido).length,
      montoCobrado: cobradasArr.reduce((s, it) => s + montoFinal(it), 0),
      montoPendiente: noCobradas.reduce((s, it) => s + montoFinal(it), 0),
    };
  }, [facturacionData, esVencido]);

  const kpiBtn = (id, label, value, color, testId) => (
    <button type="button" onClick={() => setFiltroKpi(f => (f === id && id !== 'todos' ? 'todos' : id))}
      className={cn("flex items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-0.5 transition-colors",
        filtroKpi === id ? "bg-slate-900 text-white" : "hover:bg-slate-100")}
      title={id === 'todos' ? 'Ver todas' : `Filtrar: ${label.toLowerCase()}`} data-testid={`${testId}-filtro`}>
      <span className={filtroKpi === id ? "text-slate-200" : "text-slate-500"}>{label}:</span>
      <span className={cn("font-bold", filtroKpi === id ? "text-white" : color)} data-testid={testId}>{value}</span>
    </button>
  );

  return (
        <TabsContent value="cobro" className="mt-0">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-3 flex-nowrap overflow-x-auto min-w-0">
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigatePeriodo('prev')}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="w-[140px]">
                  <PeriodSelector value={selectedPeriodo} onChange={setSelectedPeriodo} placeholder="Periodo" />
                </div>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigatePeriodo('next')}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              {/* KPIs de cobro */}
              <div className="flex items-center gap-3 flex-nowrap text-sm shrink-0 pl-1" data-testid="cobro-kpis">
                {kpiBtn('todos', 'Total', cobroKpis.total, 'text-slate-900', 'cobro-kpi-total')}
                <div className="h-4 w-px bg-slate-300 shrink-0"></div>
                {kpiBtn('pendientes', 'Pendientes', cobroKpis.pendientes, cobroKpis.pendientes > 0 ? 'text-amber-600' : 'text-slate-900', 'cobro-kpi-pendientes')}
                <div className="h-4 w-px bg-slate-300 shrink-0"></div>
                {kpiBtn('vencidos', 'Vencidos', cobroKpis.vencidos, cobroKpis.vencidos > 0 ? 'text-red-600' : 'text-slate-900', 'cobro-kpi-vencidos')}
                <div className="h-4 w-px bg-slate-300 shrink-0"></div>
                <div className="flex items-center gap-1.5 whitespace-nowrap">
                  <span className="text-slate-500">Cobrado:</span>
                  <span className="font-bold text-green-700" data-testid="cobro-kpi-cobrado">$ {formatImporte(cobroKpis.montoCobrado, 2)}</span>
                </div>
                <div className="h-4 w-px bg-slate-300 shrink-0"></div>
                <div className="flex items-center gap-1.5 whitespace-nowrap">
                  <span className="text-slate-500">Pendiente:</span>
                  <span className="font-bold text-amber-600" data-testid="cobro-kpi-monto-pendiente">$ {formatImporte(cobroKpis.montoPendiente, 2)}</span>
                </div>
              </div>
            </div>
            <ExtractoBancarioButton edificioId={edificioId} periodo={selectedPeriodo}
              onDone={async () => { try { const r = await facturacionApi.getMensual(edificioId, selectedPeriodo); setFacturacionData(r.data); } catch { /* recarga manual */ } }} />
          </div>
          {loadingFacturacion ? (
            <LoadingState text="Cargando datos de cobro..." />
          ) : !facturacionData || facturacionData.items.length === 0 ? (
            <EmptyState icon={CreditCard} title="Sin unidades en administracion" description="No hay unidades en administracion para este edificio." />
          ) : (
            <div className="flex-1 min-h-0 flex flex-col">
              <div className="bg-white border-x border-b border-slate-200 rounded-b-lg overflow-auto -mt-px flex-1 min-h-0">
                <table className={cn("w-full text-sm [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:bg-slate-50 [&_tfoot_td]:sticky [&_tfoot_td]:bottom-0 [&_tfoot_td]:z-10 [&_tfoot_td]:bg-slate-100", filtroKpi === 'todos' && "min-h-full")}>
                  <colgroup>
                    <col style={{width: '7%'}} />
                    <col style={{width: '11%'}} />
                    <col style={{width: '11%'}} />
                    <col style={{width: '11%'}} />
                    <col style={{width: '11%'}} />
                    <col style={{width: '11%'}} />
                    <col style={{width: '11%'}} />
                    <col style={{width: '11%'}} />
                    <col style={{width: '3%'}} />
                  </colgroup>
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="py-2.5 px-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Unidad</th>
                      <th className="py-2.5 px-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Inquilino</th>
                      <th className="py-2.5 px-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Capital</th>
                      <th className="py-2.5 px-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Intereses</th>
                      <th className="py-2.5 px-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Bonif.</th>
                      <th className="py-2.5 px-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Total</th>
                      <th className="py-2.5 px-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Estado</th>
                      <th className="py-2.5 px-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Fecha Cobro</th>
                      <th className="py-2.5 px-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Sem.</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(getGroupedFacItems ? getGroupedFacItems(facturacionData.items.filter(item => item.tiene_contrato && pasaFiltro(item))) : facturacionData.items.filter(item => item.tiene_contrato && pasaFiltro(item)).map(item => ({ type: 'item', item })))
                    .map((entry) => {
                      if (entry.type === 'group_summary') {
                        // Grupo (ej. Piso 1) cerrado: solo totales. Abierto: cada unidad con estado, fecha y semáforo.
                        const gi = entry.items;
                        const sum = (fn) => gi.reduce((s, i) => s + fn(i), 0);
                        const abierto = expandedGrupos[entry.grupo.grupo_id];
                        const bonif = sum(i => i.bonificacion_intereses || 0);
                        return (
                          <tr key={`gs-${entry.grupo.grupo_id}`} className="bg-indigo-50 hover:bg-indigo-100/60 font-semibold cursor-pointer"
                            onClick={() => toggleGrupoExpanded(entry.grupo.grupo_id)} data-testid={`cobro-grupo-${entry.grupo.grupo_id}`}>
                            <td className="py-2.5 px-3 text-indigo-900">
                              <span className="inline-flex items-center gap-1.5">
                                {abierto ? <ChevronUp className="w-4 h-4 text-indigo-400" /> : <ChevronDown className="w-4 h-4 text-indigo-400" />}
                                {entry.grupo.nombre}
                              </span>
                            </td>
                            <td className="py-2.5 px-3 text-xs text-indigo-500">{gi.length} unidades</td>
                            <td className="py-2.5 px-3 text-right font-mono text-sm text-indigo-900 whitespace-nowrap">$ {formatImporte(sum(i => (i.monto_neto || 0) + (i.iva || 0)), 2)}</td>
                            <td className="py-2.5 px-3 text-right font-mono text-sm text-indigo-400 whitespace-nowrap">{sum(i => i.intereses || 0) > 0 ? `$ ${formatImporte(sum(i => i.intereses || 0), 2)}` : '-'}</td>
                            <td className="py-2.5 px-3 text-right font-mono text-sm text-indigo-400 whitespace-nowrap">{bonif > 0 ? `- $ ${formatImporte(bonif, 2)}` : '-'}</td>
                            <td className="py-2.5 px-3 text-right font-mono text-sm text-green-700 whitespace-nowrap" data-testid={`cobro-grupo-total-${entry.grupo.grupo_id}`}>$ {formatImporte(sum(i => i.total || 0) - bonif, 2)}</td>
                            <td colSpan={3}></td>
                          </tr>
                        );
                      }
                      const item = entry.item;
                      const enGrupo = !!entry.grupoId;
                      const hoy = new Date();
                      const estaCobrada = item.estado_factura === 'cobrada';
                      const esParcial = item.estado_factura === 'cobro_parcial';
                      const isExpanded = expandedRows[item.unidad_id];

                      // Semáforo: comparar mes del período vs mes actual
                      const [pYear, pMonth] = selectedPeriodo.split('-').map(Number);
                      const mesActual = hoy.getFullYear() * 12 + hoy.getMonth() + 1;
                      const mesPeriodo = pYear * 12 + pMonth;
                      let semaforoColor = 'green';
                      if (!estaCobrada && item.tiene_contrato) {
                        if (mesPeriodo < mesActual) {
                          // Mes anterior = siempre rojo
                          semaforoColor = 'red';
                        } else if (mesPeriodo === mesActual) {
                          const diaActual = hoy.getDate();
                          if (diaActual >= 16) semaforoColor = 'red';
                          else if (diaActual >= 11) semaforoColor = 'yellow';
                        }
                      }

                      return (
                        <React.Fragment key={item.unidad_id}>
                          <tr className={cn("hover:bg-slate-100/50", esParcial && "bg-amber-50/30", enGrupo && "bg-indigo-50/40")}>
                            <td className={cn("py-2.5 px-3 font-medium text-slate-900", enGrupo && "border-l-4 border-l-indigo-400 pl-6")}>
                              {esParcial ? (
                                <button onClick={() => toggleRow(item.unidad_id)} className="flex items-center gap-1 hover:text-blue-600 transition-colors">
                                  {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-amber-500" /> : <ChevronDown className="w-3.5 h-3.5 text-amber-500" />}
                                  {item.unidad_nombre}
                                </button>
                              ) : item.unidad_nombre}
                            </td>
                            <td className="py-2.5 px-3 text-slate-600">{item.nombre_comercial || item.inquilino || '-'}</td>
                            <td className="py-2.5 px-3 text-right font-mono text-sm text-slate-700 whitespace-nowrap">
                              $ {formatImporte((item.monto_neto || 0) + (item.iva || 0), 2)}
                            </td>
                            <td className="py-2.5 px-3 text-right font-mono text-sm whitespace-nowrap">
                              {(item.intereses || 0) > 0 ? (
                                <span className="text-orange-600">$ {formatImporte(item.intereses, 2)}</span>
                              ) : <span className="text-slate-300">-</span>}
                            </td>
                            <td className="py-2.5 px-3 text-right font-mono text-sm whitespace-nowrap">
                              {(item.intereses || 0) > 0 ? (
                                <BonificacionCell
                                  item={item}
                                  edificioId={edificioId}
                                  selectedPeriodo={selectedPeriodo}
                                  setFacturacionData={setFacturacionData}
                                  toast={toast}
                                  formatImporte={formatImporte}
                                />
                              ) : <span className="text-slate-300">-</span>}
                            </td>
                            <td className="py-2.5 px-3 text-right font-mono text-sm font-semibold text-green-700 whitespace-nowrap">
                              $ {formatImporte((item.total || 0) - (item.bonificacion_intereses || 0), 2)}
                            </td>
                            <td className="py-2.5 px-3 text-center">
                              <Select
                                value={item.estado_factura || 'pendiente'}
                                onValueChange={async (value) => {
                                  try {
                                    await facturacionApi.updateStatus(edificioId, item.unidad_id, selectedPeriodo, { estado: value });
                                    const res = await facturacionApi.getMensual(edificioId, selectedPeriodo);
                                    setFacturacionData(res.data);
                                    toast.success('Estado actualizado');
                                    if (value === 'cobro_parcial') setExpandedRows(prev => ({ ...prev, [item.unidad_id]: true }));
                                  } catch (err) { toast.error('Error al actualizar estado'); }
                                }}
                              >
                                <SelectTrigger className={cn(
                                  "h-7 text-xs w-[110px]",
                                  (item.estado_factura === 'pendiente' || !item.estado_factura) && "border-slate-300 text-slate-600",
                                  item.estado_factura === 'creada' && "border-blue-300 bg-blue-50 text-blue-700",
                                  item.estado_factura === 'enviada' && "border-purple-300 bg-purple-50 text-purple-700",
                                  item.estado_factura === 'cobro_parcial' && "border-amber-300 bg-amber-50 text-amber-700",
                                  item.estado_factura === 'cobrada' && "border-green-300 bg-green-50 text-green-700",
                                  item.estado_factura === 'vencida' && "border-red-300 bg-red-50 text-red-700"
                                )}>
                                  <SelectValue placeholder="Pendiente" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pendiente">Pendiente</SelectItem>
                                  <SelectItem value="vencida">Vencida</SelectItem>
                                  <SelectItem value="creada">Creada</SelectItem>
                                  <SelectItem value="enviada">Enviada</SelectItem>
                                  <SelectItem value="cobro_parcial">Cobro Parcial</SelectItem>
                                  <SelectItem value="cobrada">Cobrada</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="py-2.5 px-3 text-center">
                              <FechaCobro
                                initialValue={item.fecha_cobro}
                                onSave={async (isoDate) => {
                                  try {
                                    await facturacionApi.updateStatus(edificioId, item.unidad_id, selectedPeriodo, { estado: item.estado_factura, fecha_cobro: isoDate });
                                    const res = await facturacionApi.getMensual(edificioId, selectedPeriodo);
                                    setFacturacionData(res.data);
                                    toast.success('Fecha actualizada');
                                  } catch (err) { toast.error('Error al actualizar fecha'); }
                                }}
                              />
                            </td>
                            <td className="py-2.5 px-3 text-center">
                              <div className={cn(
                                "w-4 h-4 rounded-full mx-auto",
                                semaforoColor === 'green' && "bg-green-500",
                                semaforoColor === 'yellow' && "bg-yellow-400",
                                semaforoColor === 'red' && "bg-red-500"
                              )} title={estaCobrada ? "Cobrada" : semaforoColor === 'green' ? "En termino (hasta dia 10)" : semaforoColor === 'yellow' ? "Alerta (dia 11-15)" : "Vencido (dia 16+)"} />
                            </td>
                          </tr>
                          {esParcial && isExpanded && (
                            <CobrosParciales
                              item={item}
                              edificioId={edificioId}
                              selectedPeriodo={selectedPeriodo}
                              setFacturacionData={setFacturacionData}
                              toast={toast}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                    {(() => {
                      const items = facturacionData.items.filter(i => i.tiene_contrato);
                      const totalBonif = items.reduce((s, i) => s + (i.bonificacion_intereses || 0), 0);
                      return (
                        <tr className="font-semibold">
                          <td colSpan={2} className="py-3 px-3 text-right text-slate-700">Total:</td>
                          <td className="py-3 px-3 text-right font-mono text-slate-700 whitespace-nowrap">
                            $ {formatImporte((facturacionData.totales.neto || 0) + (facturacionData.totales.iva || 0), 2)}
                          </td>
                          <td className="py-3 px-3 text-right font-mono text-orange-600 whitespace-nowrap">
                            {(facturacionData.totales.intereses || 0) > 0 ? `$ ${formatImporte(facturacionData.totales.intereses, 2)}` : '-'}
                          </td>
                          <td className="py-3 px-3 text-right font-mono text-purple-600 whitespace-nowrap">
                            {totalBonif > 0 ? `- $ ${formatImporte(totalBonif, 2)}` : '-'}
                          </td>
                          <td className="py-3 px-3 text-right font-mono text-green-700 whitespace-nowrap">
                            $ {formatImporte((facturacionData.totales.total || 0) - totalBonif, 2)}
                          </td>
                          <td colSpan={3}></td>
                        </tr>
                      );
                    })()}
                  </tfoot>
                </table>
              </div>
              <div className="mt-3 flex items-center gap-4 text-xs text-slate-500 justify-end">
                <span className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-green-500" />En termino (dia 1-10)</span>
                <span className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-yellow-400" />Alerta (dia 11-15)</span>
                <span className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-red-500" />Vencido (dia 16+)</span>
              </div>
            </div>
          )}
        </TabsContent>
  );
};
